import {
  ReactNode,
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  MouseEventHandler,
} from 'react';
import styles from './CTAButton.module.scss';
import { icons } from '@/assets/icons';
import clsx from 'clsx';
import Link from 'next/link';
import { UrlObject } from 'url';
import { type Route } from 'next';

interface BaseProps {
  variant?: null | 'orange' | 'white' | 'gray';
  className?: string;
  iconId?: string;
}

interface ButtonProps
  extends BaseProps,
    ButtonHTMLAttributes<HTMLButtonElement> {
  mode: 'button';
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  href?: never;
}

interface LinkProps
  extends BaseProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick' | 'href'> {
  mode: 'link';
  children: ReactNode;
  href: Route | UrlObject;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  disabled?: never;
  type?: never;
}

type CTAButtonProps = ButtonProps | LinkProps;

export default function CTAButton({
  children,
  variant,
  className = '',
  iconId = 'icon-arrow_rounded',
  ...props
}: CTAButtonProps) {
  const content = (
    <>
      <svg className={styles.icon} viewBox="0 0 32 32">
        <use xlinkHref={`${icons.src}#${iconId}`} />
      </svg>
      <span className={styles.text}>{children}</span>
    </>
  );

  const commonClasses = clsx(
    styles.button,
    variant && styles[variant],
    className
  );

  if (props.mode === 'link') {
    const { href, target, onClick, ...restLinkProps } = props as LinkProps;
    return (
      <Link
        href={href}
        className={commonClasses}
        target={target}
        onClick={onClick}
        {...restLinkProps}
      >
        {content}
      </Link>
    );
  }

  const {
    onClick,
    disabled = false,
    type = 'button',
    ...restButtonProps
  } = props as ButtonProps;

  return (
    <button
      className={commonClasses}
      onClick={onClick}
      disabled={disabled}
      type={type}
      {...restButtonProps}
    >
      {content}
    </button>
  );
}
